import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import GlassyModal from 'components/ui/GlassyModal';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { db, storage } from '../../firebaseClient'; // Firebase services
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import Notification from 'components/ui/Notification';
import SimplePhoneInput from 'components/ui/SimplePhoneInput';

const AssignmentSubmissionForm = ({ isOpen, onClose, onSubmit, initialSubject = '' }) => {
  const { user, profile } = useAuth(); // Get user and profile from Firebase
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [otherSubject, setOtherSubject] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  // Get current date for pre-filling
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth() + 1; // getMonth() returns 0-11
  const currentYear = today.getFullYear();
  const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD format

  const { register, handleSubmit, control, formState: { errors }, reset, watch, setValue, trigger } = useForm({
    defaultValues: {
      assignment_title: '',
      subject: initialSubject || '',
      custom_subject: '',
      assignmentType: '',
      deadlineDay: currentDay.toString(),
      deadlineMonth: currentMonth.toString(),
      deadlineYear: currentYear.toString(),
      pages: 1,
      instructions: '',
      preferred_communication: '',
      phoneNumber: '',
      additional_notes: '',
      attachments: []
    }
  });

  // Import subjects from the categories in ExpertiseSection
  const subjects = [
    // STEM & Engineering
    'Mathematics', 'Computer Science', 'Physics', 'Chemistry', 'Engineering', 'Statistics',
    // Business & Economics
    'Business Administration', 'Economics', 'Marketing', 'Finance', 'Management', 'Accounting',
    // Humanities & Social Sciences
    'English Literature', 'History', 'Psychology', 'Sociology', 'Philosophy', 'Political Science',
    // Health & Life Sciences
    'Nursing', 'Biology', 'Medicine', 'Public Health', 'Pharmacy', 'Nutrition',
    // Other
    'Other'
  ];

  // Set otherSubject state if initialSubject is not in the subjects list
  useEffect(() => {
    if (initialSubject && !subjects.includes(initialSubject)) {
      setValue('subject', 'Other');
      setValue('custom_subject', initialSubject);
      setOtherSubject(true);
    }
  }, [initialSubject, setValue, subjects]);

  useEffect(() => {
    console.log('User:', user);
    console.log('Profile:', profile);
  }, [user, profile]);

  const handleRemoveAttachment = (indexToRemove) => {
    const currentAttachments = watch('attachments');
    const updatedAttachments = currentAttachments.filter((_, index) => index !== indexToRemove);
    setValue('attachments', updatedAttachments);
  };

  const totalSteps = 3;
  const watchSubject = watch('subject');
  const watchAssignmentType = watch('assignmentType');

  const assignmentTypes = [
    'Paper', 'Platform'
  ];

  const communicationModes = [
    'WhatsApp', 'Discord', 'Email (Coming Soon)'
  ];

  const handleNextStep = async () => {
    const fieldsToValidate = {
      1: ['assignment_title', 'subject', 'custom_subject', 'assignmentType', 'deadlineDay', 'deadlineMonth', 'deadlineYear', 'pages', 'preferred_communication', 'phoneNumber'],
      2: ['instructions'],
    }[step];

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleFormSubmit = async (data) => {
    setLoading(true);
    console.log('Submitting assignment with data:', data);
    console.log('User:', user);
    console.log('Profile:', profile);

    try {
      // Upload any attachments to Firebase storage
      const attachmentUrls = [];

      if (data.attachments && data.attachments.length > 0) {
        setUploading(true);
        setUploadProgress(0);

        const uploadPromises = data.attachments.map(file => {
          return new Promise((resolve, reject) => {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
            const storageRef = ref(storage, `${user.uid}/assignments/${fileName}`);

            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
              },
              (error) => {
                console.error('Error uploading file:', error);
                setNotification({ show: true, message: 'Error uploading file. Please try again.', type: 'error' });
                setUploading(false);
                reject(error);
              },
              async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                attachmentUrls.push({
                  name: file.name,
                  url: downloadURL,
                  type: file.type,
                  size: file.size
                });
                resolve();
              }
            );
          });
        });

        await Promise.all(uploadPromises);
        setUploading(false);
        console.log('All files uploaded. Attachment URLs:', attachmentUrls);
      }

      // Determine the final subject value (use custom_subject if subject is "Other")
      const finalSubject = data.subject === 'Other' ? data.custom_subject : data.subject;

      // Use the selected communication value directly
      const finalCommunication = data.preferred_communication;

      // Combine date fields into a single date with comprehensive validation
      let day = parseInt(data.deadlineDay?.toString()?.trim());
      let month = parseInt(data.deadlineMonth?.toString()?.trim());
      let year = parseInt(data.deadlineYear?.toString()?.trim());
      
      // Validate individual date components
      if (!day || day < 1 || day > 31) {
        console.warn('Invalid day, using current day:', day);
        day = currentDay;
      }
      if (!month || month < 1 || month > 12) {
        console.warn('Invalid month, using current month:', month);
        month = currentMonth;
      }
      if (!year || year < currentYear) {
        console.warn('Invalid year, using current year:', year);
        year = currentYear;
      }
      
      console.log('Creating date with:', { day, month, year });
      
      // Create a valid date object (month is 0-indexed in JavaScript Date)
      const deadlineDate = new Date(year, month - 1, day, 23, 59, 59); // Set to end of day
      
      // Validate the created date
      let finalDeadline = deadlineDate;
      if (isNaN(deadlineDate.getTime())) {
        console.error('Failed to create valid date with:', { day, month, year });
        // Fallback to tomorrow as deadline
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(23, 59, 59, 999);
        console.log('Using fallback date:', tomorrow);
        finalDeadline = tomorrow;
      }
      
      console.log('Final deadline date:', finalDeadline);

      // Prepare assignment data with proper validation
      const assignmentData = {
        user_id: user?.uid || '',
        email: user?.email || '',
        name: profile?.full_name || user?.displayName || '',
        assignment_title: data.assignment_title?.trim() || '',
        subject: finalSubject?.trim() || '',
        assignment_type: data.assignmentType?.trim() || '',
        deadline: finalDeadline,
        pages: Math.max(1, parseInt(data.pages) || 1),
        requirements: data.instructions?.trim() || '',
        preferred_communication: finalCommunication?.trim() || '',
        phone_number: data.phoneNumber?.trim() || '',
        additional_notes: data.additional_notes?.trim() || '',
        attachments: attachmentUrls || [],
        status: 'pending',
        created_at: serverTimestamp()
      };
      
      // Additional validation
      if (!assignmentData.user_id) {
        throw new Error('User authentication required');
      }
      if (!assignmentData.assignment_title) {
        throw new Error('Assignment title is required');
      }
      if (!assignmentData.subject) {
        throw new Error('Subject is required');
      }

      console.log('Assignment data to be saved:', assignmentData);

      // Save assignment to Firestore with retry logic
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          console.log(`Attempting to save assignment (attempt ${retryCount + 1}/${maxRetries + 1})`);
          const docRef = await addDoc(collection(db, 'assignments'), assignmentData);
          console.log('Assignment saved successfully with ID:', docRef.id);

          // Don't call onSubmit callback until user manually closes modal
          // This prevents parent component from auto-closing the modal

          // Show success notification
          setNotification({ 
            show: true, 
            message: 'Your assignment has been submitted successfully! We will get back to you shortly.', 
            type: 'success' 
          });
          setIsSubmitted(true);
          
          // Break out of retry loop on success
          break;

        } catch (firestoreError) {
          console.error(`Firestore error (attempt ${retryCount + 1}):`, {
            message: firestoreError.message,
            code: firestoreError.code,
            stack: firestoreError.stack,
            assignmentData: JSON.stringify(assignmentData, null, 2)
          });
          
          retryCount++;
          
          if (retryCount > maxRetries) {
            // Final attempt failed, throw the error
            throw firestoreError;
          } else {
            // Wait before retrying
            console.log(`Retrying in 1 second... (${retryCount}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

    } catch (error) {
      console.error('Final error submitting assignment:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
        name: error.name
      });
      
      let errorMessage = 'There was an error submitting your assignment. Please try again.';
      
      if (error.message.includes('User authentication required')) {
        errorMessage = 'Please sign in again and try submitting your assignment.';
      } else if (error.message.includes('Assignment title is required')) {
        errorMessage = 'Please fill in the assignment title and try again.';
      } else if (error.message.includes('Subject is required')) {
        errorMessage = 'Please select a subject and try again.';
      } else if (error.message.includes('Invalid date')) {
        errorMessage = 'Please check the deadline date and try again.';
      } else if (error.code === 'permission-denied') {
        errorMessage = 'You do not have permission to submit assignments. Please contact support.';
      } else if (error.code === 'network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else if (error.code === 'invalid-argument') {
        errorMessage = 'Invalid data provided. Please check your form inputs and try again.';
      }
      
      setNotification({ show: true, message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-white mb-2 font-medium">Assignment Title <span className="text-red-400">*</span></label>
              <input
                type="text"
                {...register('assignment_title', { required: 'Assignment title is required' })}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 placeholder-white/50"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
                placeholder="Enter the title of your assignment"
              />
              {errors.assignment_title && <p className="mt-1 text-red-400 text-sm">{errors.assignment_title.message}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white mb-2 font-medium">Subject <span className="text-red-400">*</span></label>
                <select
                  {...register('subject', { 
                    required: 'Subject is required',
                    onChange: (e) => {
                      if (e.target.value === 'Other') {
                        setOtherSubject(true);
                      } else {
                        setOtherSubject(false);
                      }
                    }
                  })}
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="" className="bg-gray-800">Select a subject</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject} className="bg-gray-800">{subject}</option>
                  ))}
                </select>
                {errors.subject && <p className="mt-1 text-red-400 text-sm">{errors.subject.message}</p>}

                {otherSubject && (
                  <div className="mt-3">
                    <input
                      type="text"
                      {...register('custom_subject', { 
                        required: otherSubject ? 'Please specify the subject' : false 
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                      placeholder="Please specify the subject"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {errors.custom_subject && <p className="mt-1 text-red-400 text-sm">{errors.custom_subject.message}</p>}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-white mb-2 font-medium">Assignment Type <span className="text-red-400">*</span></label>
                <select
                  {...register('assignmentType', { required: 'Assignment type is required' })}
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="" className="bg-gray-800">Select assignment type</option>
                  {assignmentTypes.map(type => (
                    <option key={type} value={type} className="bg-gray-800">{type}</option>
                  ))}
                </select>
                {errors.assignmentType && <p className="mt-1 text-red-400 text-sm">{errors.assignmentType.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white mb-2 font-medium">Deadline <span className="text-red-400">*</span></label>
                <div className="grid grid-cols-3 gap-3">
                  {/* Day Input */}
                  <div>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('deadlineDay', { 
                        required: 'Day is required',
                        min: { value: 1, message: 'Day must be between 1-31' },
                        max: { value: 31, message: 'Day must be between 1-31' }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Day</label>
                  </div>

                  {/* Month Input */}
                  <div>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('deadlineMonth', { 
                        required: 'Month is required',
                        min: { value: 1, message: 'Month must be between 1-12' },
                        max: { value: 12, message: 'Month must be between 1-12' }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Month</label>
                  </div>

                  {/* Year Input */}
                  <div>
                    <input
                      type="number"
                      min={currentYear}
                      max={currentYear + 10}
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('deadlineYear', { 
                        required: 'Year is required',
                        min: { value: currentYear, message: `Year must be ${currentYear} or later` }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Year</label>
                  </div>
                </div>
                {(errors.deadlineDay || errors.deadlineMonth || errors.deadlineYear) && (
                  <p className="mt-1 text-red-400 text-sm">Please enter a valid date</p>
                )}
              </div>

              <div>
                <label className="block text-white mb-2 font-medium">Number of Pages <span className="text-red-400">*</span></label>
                <input
                  type="number"
                  min="1"
                  {...register('pages', { 
                    required: 'Number of pages is required',
                    min: { value: 1, message: 'Minimum 1 page' }
                  })}
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 placeholder-white/50"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                  }}
                  placeholder="Number of pages"
                />
                {errors.pages && <p className="mt-1 text-red-400 text-sm">{errors.pages.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Preferred Mode of Communication <span className="text-red-400">*</span></label>
              <select
                {...register('preferred_communication', { 
                  required: 'Preferred mode of communication is required'
                })}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
              >
                <option value="" className="bg-gray-800 text-white">Select preferred mode</option>
                {communicationModes.map(mode => {
                  const isComingSoon = mode.includes('Coming Soon');
                  return (
                    <option 
                      key={mode} 
                      value={isComingSoon ? '' : mode} 
                      className={`bg-gray-800 ${isComingSoon ? 'text-gray-500' : 'text-white'}`}
                      disabled={isComingSoon}
                    >
                      {mode}
                    </option>
                  );
                })}
              </select>
              {errors.preferred_communication && <p className="mt-1 text-red-400 text-sm">{errors.preferred_communication.message}</p>}
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Phone Number <span className="text-red-400">*</span></label>
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: 'Phone number is required',
                  validate: (value) => {
                    if (!value) return 'Phone number is required';
                    if (!value.startsWith('+')) return 'Please include country code (e.g., +254)';
                    if (value.length < 8) return 'Phone number too short';
                    return true;
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <SimplePhoneInput
                    value={value || ''}
                    onChange={onChange}
                    error={errors.phoneNumber?.message}
                  />
                )}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <>
            <div className="space-y-6">
              <div>
                <label className="block text-white mb-2 font-medium">Assignment Instructions/Requirements <span className="text-red-400">*</span></label>
                <textarea
                  {...register('instructions', { required: 'Instructions are required' })}
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-32 placeholder-white/50"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                  }}
                  placeholder="Provide detailed instructions for your assignment"
                ></textarea>
                {errors.instructions && <p className="mt-1 text-red-400 text-sm">{errors.instructions.message}</p>}
              </div>

              <div>
                <label className="block text-white mb-2 font-medium">Additional Notes (Optional)</label>
                <textarea
                  {...register('additional_notes')}
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-24 placeholder-white/50"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                  }}
                  placeholder="Any other details or preferences"
                ></textarea>
              </div>

              <div>
                <label className="block text-white mb-2 font-medium">Attachments (Optional)</label>
                <p className="text-white/60 text-sm mb-3">Upload documents (PDF, DOC, DOCX, TXT) up to 10MB each</p>
                <div className="relative mb-4">
                  <input
                    type="file"
                    id="attachments"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.rtf,.odt"
                    className="hidden"
                    onChange={(e) => {
                      try {
                        const newFiles = Array.from(e.target.files);
                        const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
                        
                        // Validate file size and type
                        const validFiles = [];
                        const invalidFiles = [];
                        
                        newFiles.forEach(file => {
                          if (file.size > maxFileSize) {
                            invalidFiles.push(`${file.name} (too large)`);
                          } else {
                            validFiles.push(file);
                          }
                        });
                        
                        if (invalidFiles.length > 0) {
                          setNotification({ 
                            show: true, 
                            message: `Files exceeding 10MB limit: ${invalidFiles.join(', ')}`, 
                            type: 'error' 
                          });
                        }
                        
                        if (validFiles.length > 0) {
                          const currentAttachments = watch('attachments') || [];
                          const combinedFiles = [...currentAttachments, ...validFiles];
                          setValue('attachments', combinedFiles);
                        }
                        
                        setUploadError(null);
                      } catch (error) {
                        console.error('Error handling file upload:', error);
                        setNotification({ show: true, message: 'There was an error uploading your files. Please try again.', type: 'error' });
                      }
                    }}
                  />
                  <div className="flex justify-center">
                    <label htmlFor="attachments" className="cursor-pointer inline-flex items-center justify-center w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
                      <Icon name="Plus" size={24} className="text-white/80" />
                    </label>
                  </div>
                </div>
              </div>

                {watch('attachments') && watch('attachments').length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-white/80 text-sm mb-2">Selected Files:</p>
                    {watch('attachments').map((file, index) => (
                      <div key={file.name + index} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-3 glassy-card">
                        <div className="flex items-center">
                          <Icon name="FileText" size={16} className="text-white/60 mr-2" />
                          <span className="text-white text-sm truncate">{file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          className="text-white/60 hover:text-red-400 transition-colors"
                          aria-label={`Remove ${file.name}`}
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {uploading && (
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-white/80 text-sm">Uploading...</span>
                      <span className="text-white/80 text-sm">{Math.round(uploadProgress)}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-progressBar"
                        style={{ width: `${uploadProgress}%`, backgroundSize: '200% 100%' }}
                      ></div>
                    </div>
                  </div>
                )}
            </div>
          </>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-white/5 rounded-lg p-6">
              <h4 className="text-white text-lg font-medium mb-4">Assignment Summary</h4>

              <div className="mb-4">
                <p className="text-white/60 text-sm">Assignment Title</p>
                <p className="text-white font-medium">{watch('assignment_title')}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-white/60 text-sm">Subject</p>
                  <p className="text-white font-medium">{watchSubject}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">Assignment Type</p>
                  <p className="text-white font-medium">{watchAssignmentType}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-white/60 text-sm">Deadline</p>
                  <p className="text-white font-medium">
                    {(() => {
                      const day = watch('deadlineDay');
                      const month = watch('deadlineMonth');
                      const year = watch('deadlineYear');
                      const monthNames = [
                        'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'
                      ];
                      if (day && month && year) {
                        const monthIndex = parseInt(month) - 1;
                        return `${day} ${monthNames[monthIndex]} ${year}`;
                      }
                      return 'Not set';
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">Pages</p>
                  <p className="text-white font-medium">{watch('pages')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-white/60 text-sm">Preferred Communication</p>
                  <p className="text-white font-medium">{watch('preferred_communication')}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">Phone Number</p>
                  <p className="text-white font-medium">{watch('phoneNumber')}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-white/60 text-sm">Requirements</p>
                <p className="text-white">{watch('instructions')}</p>
              </div>

              {watch('additional_notes') && (
                <div className="mb-4">
                  <p className="text-white/60 text-sm">Additional Notes</p>
                  <p className="text-white">{watch('additional_notes')}</p>
                </div>
              )}

              {/* Display attachments if any */}
            </div>

            <div className="bg-white/5 rounded-lg p-6">
              <h4 className="text-white text-lg font-medium mb-4">Pricing</h4>
              <div className="flex justify-between mb-2">
                <span className="text-white/80">{watch('pages')} page(s) × $8.00</span>
                <span className="text-white font-medium">${(watch('pages') * 8).toFixed(2)}</span>
              </div>
              <div className="border-t border-white/10 mt-4 pt-4 flex justify-between">
                <span className="text-white font-medium">Total</span>
                <span className="text-accent font-bold text-xl">${(watch('pages') * 8).toFixed(2)}</span>
              </div>
            </div>

            {isSubmitted && (
              <div className="bg-white/5 rounded-lg p-6">
                <div className="flex items-center text-purple-500 mb-2">
                  <Icon name="Info" size={20} className="mr-2" />
                  <h4 className="text-white text-lg font-medium">Thank you for your submission!</h4>
                </div>
                <p className="text-white/80">
                  Our team is reviewing your assignment details and will get back to you shortly.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <GlassyModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        reset();
        setStep(1);
        setIsSubmitted(false);
      }}
      title="Submit Assignment"
      icon="FileText"
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} onClick={(e) => e.stopPropagation()}>
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <React.Fragment key={index}>
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step > index + 1 ? 'bg-purple-500 text-white' : 
                    step === index + 1 ? 'bg-white/20 text-white border-2 border-purple-500' : 
                    'bg-white/10 text-white/60'
                  }`}
                >
                  {step > index + 1 ? <Icon name="Check" size={16} /> : index + 1}
                </div>
                {index < totalSteps - 1 && (
                  <div className={`flex-1 h-1 ${step > index + 1 ? 'bg-purple-500' : 'bg-white/10'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-white/60 text-sm">Details</span>
            <span className="text-white/60 text-sm">Instructions</span>
            <span className="text-white/60 text-sm">Review</span>
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-4 border-t border-white/10">
          {step > 1 ? (
            <button
              type="button"
              onClick={handlePrevStep}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white flex items-center"
            >
              <Icon name="ArrowLeft" size={18} className="mr-2" />
              Back
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
            >
              Cancel
            </button>
          )}

          {step < totalSteps ? (
            <button
              type="button"
              onClick={handleNextStep}
              className="px-6 py-2 bg-accent hover:bg-accent-600 rounded-lg text-white flex items-center"
            >
              {step === totalSteps - 1 ? 'Finish' : 'Next'}
              <Icon name="ArrowRight" size={18} className="ml-2" />
            </button>
          ) : isSubmitted ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                reset();
                setStep(1);
                setIsSubmitted(false);
              }}
              className="px-6 py-2 bg-accent hover:bg-accent-600 rounded-lg text-white flex items-center justify-center"
            >
              Close
            </button>
          ) : loading ? (
            <button
              type="button"
              disabled
              className="px-6 py-2 bg-accent-600 cursor-not-allowed rounded-lg text-white flex items-center justify-center opacity-80"
            >
              <Icon name="Loader2" size={18} className="animate-spin mr-2" />
              Submitting...
            </button>
          ) : (
            <button
              type="submit"
              className="px-6 py-2 bg-accent hover:bg-accent-600 rounded-lg text-white flex items-center"
            >
              Submit Assignment
              <Icon name="Send" size={18} className="ml-2" />
            </button>
          )}
        </div>
      </form>
    </GlassyModal>
  );
};

export default AssignmentSubmissionForm;
